import { Color3, Vector3 } from '@babylonjs/core';

export class LightConfig {
  constructor(
    // hemispheric light properties

    /**
     * The direction of the hemispheric light source. This is a normalized vector indicating where the light is coming from.
     * It affects the overall ambient light in the scene.
     */
    public hemiDirection: Vector3 = new Vector3(0, 1, 0),
    /**
     * The intensity of the hemispheric light source. This controls how bright the ambient light is.
     * Higher values yield brighter ambient light, while lower values yield dimmer ambient light.
     * This value is typically lower than the directional light intensity to create a more natural lighting effect.
     */
    public hemiIntensity: number = 0.6,

    /**
     * The ground color of the hemispheric light source. This controls the color of the ambient light.
     */
    public hemiGroundColor: Color3 = new Color3(0.3, 0.3, 0.4),

    // directional light properties

    /**
     * The direction of the light source. This is a normalized vector indicating where the light is coming from.
     * Affects the angle and spread of the light.
     */
    public direction: Vector3 = new Vector3(-0.5, -0.5, 0.5),
    /**
     * The position of the light source in the scene. This is where the light is located.
     * It can be used to simulate sunlight or other directional lights.
     */
    public position: Vector3 = new Vector3(5, 15, -5),
    /**
     * The diffuse color of the light source. This is the color of the light that illuminates objects in the scene.
     * It affects how colors appear under the light and can be used to create different moods or atmospheres.
     */
    public diffuse: Color3 = new Color3(1, 1, 1),

    /**
     * Whether to enable lens flare effects for the light source.
     * Lens flares are visual artifacts that simulate the scattering of light in a camera lens.
     * They can add a cinematic effect to the scene, especially for bright light sources like the sun.
     */
    public lensflare: boolean = true,
    /**
     * The intensity of the light source. This controls how bright the light is.
     * Higher values yield brighter light, while lower values yield dimmer light.
     */
    public intensity: number = 4.0,

    // shadow properties

    /**
     * The darkness of the shadows. 0 means strongest and 1 would means no shadow
     */
    public darkness: number = 0,

    /**
     * The size of the shadow map texture. A larger size provides more detail but consumes more memory and processing power.
     * Quality: Higher values yield sharper shadows, but at a performance cost.
     * Performance: depending on the size.
     */
    public mapSize: number = 2048,

    // all to false for sharp shadows..

    /**
     * PCF softens shadow edges by sampling multiple points and averaging the result. This produces smooth, realistic shadow borders.
     * Quality: Good, soft shadows with natural transitions.
     * Performance: Medium. It’s a good compromise between quality and speed, suitable for most games.
     */
    public usePercentageCloserFiltering: boolean = false,

    /**
     * Uses exponential depth values to reduce shadow artifacts and smooth out shadows. Can sometimes cause a "glow" effect around shadows.
     * Quality: Very soft, sometimes too blurry. Can help reduce shadow acne and light leaks.
     * Performance: Medium to fast.
     */
    public useExponentialShadowMap: boolean = false,

    /**
     * Like ESM, but applies an additional blur filter for even softer shadows.
     * Quality: Extremely soft, almost foggy shadows. No sharp edges.
     * Performance: Slow. The blur operation is computationally expensive.
     */
    public useBlurExponentialShadowMap: boolean = false,

    /**
     * Applies a kernel-based blur to the shadow map (often used with Blur ESM).
     * Quality: Very soft, cinematic shadows. No hard edges.
     * Performance: Very slow. Only use for high-quality rendering or screenshots.
     */
    public useKernelBlur: boolean = false,

    /**
     * Uses a Poisson disk pattern to sample the shadow map, creating soft, dithered shadow edges.
     * Quality: Soft, slightly grainy shadows. Looks natural and avoids banding.
     * Performance: Medium to fast.
     */
    public usePoissonSampling: boolean = false,

    /**
     * A higher bias can help reduce acne but may cause shadows to detach from their casting objects.
     * Normal bias is an additional offset based on the surface normal, which can help with sloped surfaces.
     */
    public bias: number = 0.003,

    /**
     * Normal bias is an additional offset based on the surface normal, which can help with sloped surfaces.
     * It helps to prevent shadow acne on surfaces that are not perfectly flat.
     */
    public normalBias: number = 0.01,

    /**
     * Whether to stabilize cascades to prevent shadow acne.
     */
    public stabilizeCascades: boolean = true,

    /**
     * Lambda value for cascades.
     * Higher = more resolution near camera
     * Higher values yield more resolution near camera.
     */
    public lambda: number = 0.9,

    /**
     * Cascade blend percentage.
     * Smooth transitions between cascades
     * Higher values yield smoother transitions between cascades.
     */
    public cascadeBlendPercentage: number = 0.1,

    /**
     * Whether to enable depth clamp to prevent shadow acne.
     */
    public depthClamp: boolean = true,

    /**
     * Whether to enable auto calculation of depth bounds to prevent shadow acne.
     */
    public autoCalcDepthBounds: boolean = true,

    /**
     * Maximum distance from camera for shadow casting.
     * Objects beyond this distance won't cast shadows, improving performance.
     * Set to 0 to disable distance limiting (all registered casters cast shadows).
     */
    public shadowCasterDistance: number = 0
  ) {}
}
